import { Component, Input, OnInit, numberAttribute } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, Validators } from '@angular/forms';
import { GlobalService } from '../../../../core/_infrastructure/classes/services/global.service';
import { ComponentBase } from '../../../../core/_infrastructure/classes/componentBase';
import { NotificationService } from '../../../../core/_infrastructure/classes/services/notification.service';
import { ExceptionManager } from '../../../../core/_infrastructure/classes/handlers/exceptionManager';
import { AuthService } from '../../../../core/_infrastructure/classes/services/auth.service';
import { EnumSiNoTodos, Tc } from '../../../../core/_infrastructure/types/common';
import { SelectItem } from 'primeng/api';
import { EmpresaDeshidratadoraViewModel } from '../empresadeshidratadora.types';
import { EmpresaDeshidratadoraService } from '../empresadeshidratadora.service';
@Component({
    selector: 'app-empresadeshidratadora-edit',
    templateUrl: './empresadeshidratadora-edit.component.html',
})
export class EmpresaDeshidratadoraEditComponent extends ComponentBase implements OnInit {
    @Input() readonly = false;
    @Input({ transform: numberAttribute }) id = 0;
    @Input() dialogMode = false;

    /**
     * @article: How to Use @Input() to Read Angular Route Parameters
     * https://www.freecodecamp.org/news/use-input-for-angular-route-parameters/
     */

    listSiNoTodos: SelectItem[];
    listProvincias: Tc[];
    listMunicipios: Tc[];

    empresaDeshidratadora: EmpresaDeshidratadoraViewModel;

    visibilitySpinner = false;
    isCollapsed = false;
    isCollapsedDesautorizados = false;

    private pathEmpresasDeshidratadoras = '/mantenimiento/general/empresasdeshidratadoras/';
    private successMessageModified = 'Empresa Deshidratadora modificada correctamente';
    private successMessageAdded = 'Empresa Deshidratadora añadida correctamente';
    private errorMessage = 'Revise los datos';

    constructor(
        private router: Router,
        private fb: FormBuilder,
        private globalService: GlobalService,
        private empresaDeshidratadoraService: EmpresaDeshidratadoraService,

        protected notificationService: NotificationService,
        protected authService: AuthService,
        protected exceptionHandler: ExceptionManager,
    ) {
        super(authService, notificationService, exceptionHandler);
        this.empresaDeshidratadora = new EmpresaDeshidratadoraViewModel();
    }

    ngOnInit(): void {
        this.createForm();
        this.loadLists();
        this.processInput();
    }

    private processInput(): void {
        if (this.id) {
            this.empresaDeshidratadoraService.getEmpresaDeshidratadora(this.id).subscribe(res => {
                this.empresaDeshidratadora = res;
                this.updateForm(this.empresaDeshidratadora);
            });
        }
    }

    private loadLists(): void {
        this.globalService.getProvincias().subscribe(res => {
            this.listProvincias = res;
        });

        this.listSiNoTodos = [
            { label: 'Sí', value: EnumSiNoTodos.SI },
            { label: 'NO', value: EnumSiNoTodos.NO },
        ];
    }

    private createForm(): void {
        this.form = this.fb.group({
            NumeroRegistro: ['', Validators.required],
            NombreIndustria: ['', Validators.required],
            Direccion: ['', Validators.required],
            IdProvincia: null,
            IdMunicipio: null,
            Activo: [{ value: true, disabled: this.readonly || false }],
            Desautorizada: [{ value: true, disabled: this.readonly || false }],
        });
    }

    updateForm(empresaDeshidratadora: EmpresaDeshidratadoraViewModel): void {
        if (!empresaDeshidratadora) {
            return;
        }
        this.globalService.getMunicipios(this.empresaDeshidratadora.IdProvincia).subscribe(res => {
            this.listMunicipios = res;
        });

        this.form.patchValue({
            NumeroRegistro: empresaDeshidratadora.NumeroRegistro,
            NombreIndustria: empresaDeshidratadora.NombreIndustria,
            Direccion: empresaDeshidratadora.Direccion,
            IdProvincia: empresaDeshidratadora.IdProvincia,
            IdMunicipio: empresaDeshidratadora.IdMunicipio,
            Activo: empresaDeshidratadora.Activo,
            Desautorizada: empresaDeshidratadora.Desautorizada,
        });
    }

    onSelectProvincia(): void {
        const idProvincia = this.form.value.IdProvincia;

        this.globalService.getMunicipios(idProvincia).subscribe(res => {
            this.listMunicipios = res;
        });

        this.form.patchValue({
            IdMunicipio: null,
        });
    }

    onAdd(): void {
        this.router.navigate([this.pathEmpresasDeshidratadoras + 'create']);
    }

    onEdit(): void {
        this.router.navigate([this.pathEmpresasDeshidratadoras + 'edit']);
    }

    onSubmit(): void {
        this.submitted = true;

        if (!this.form.valid) {
            this.addErrorMessage(this.errorMessage);
            return;
        }

        this.visibilitySpinner = true;
        let empresaDeshidratadora = this.form.getRawValue() as EmpresaDeshidratadoraViewModel;
        empresaDeshidratadora.Id = this.empresaDeshidratadora.Id;

        if (this.editMode) {
            this.update(empresaDeshidratadora);
        } else {
            this.create(empresaDeshidratadora);
        }
    }

    private update(empresaDeshidratadora: EmpresaDeshidratadoraViewModel): void {
        this.empresaDeshidratadoraService.edit(empresaDeshidratadora).subscribe({
            next: () => {
                this.addSuccessMessage(this.successMessageModified);
                this.visibilitySpinner = false;
                this.onBack();
            },
            error: error => {
                this.catchServerError(error);
                this.visibilitySpinner = false;
            },
        });
    }

    private create(empresaDeshidratadora: EmpresaDeshidratadoraViewModel): void {
        this.empresaDeshidratadoraService.create(empresaDeshidratadora).subscribe({
            next: () => {
                this.addSuccessMessage(this.successMessageAdded);
                this.visibilitySpinner = false;
                this.onBack();
            },
            error: error => {
                this.catchServerError(error);
                this.addErrorMessage(this.errorMessage);
                this.visibilitySpinner = false;
            },
        });
    }

    onBack(): void {
        this.router.navigate([this.pathEmpresasDeshidratadoras]);
    }

    /**
     * Getter para controlar la propiedad disabled del botón de guardar.
     * @return boolean
     */
    get formInvalid(): boolean {
        return !this.form.valid || !this.form.dirty;
    }

    get editMode(): boolean {
        return this.empresaDeshidratadora?.Id !== undefined;
    }

    get title(): string {
        return this.editMode ? (this.readonly ? 'Datos de la ' : 'Editar ') : 'Nueva ';
    }
}
